import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { SERVICE_TICKET_KEYS } from "./service-ticket.keys";
import { serviceTicketService } from "./service-ticket.services";
import { GetServiceTicketParams } from "./service-ticket.types";

export const useGetServiceTickets = (
  params?: GetServiceTicketParams,
  enabled = true
) => {
  return useQuery({
    queryKey: SERVICE_TICKET_KEYS.GET_SERVICE_TICKETS(params),
    queryFn: () => serviceTicketService.getServiceTickets(params),
    enabled,
  });
};

export const useGetServiceTicketsForDashboard = (
  params?: GetServiceTicketParams,
  enabled = true
) => {
  return useQuery({
    queryKey: [...SERVICE_TICKET_KEYS.GET_SERVICE_TICKETS(params), 'dashboard'],
    queryFn: () => serviceTicketService.getServiceTicketsForDashboard(params),
    enabled,
    staleTime: 1 * 60 * 1000, // 1 minute for dashboard
    gcTime: 5 * 60 * 1000, // 5 minutes cache time
  });
};

export const useGetServiceTicket = (id?: string | null) => {
  return useQuery({
    queryKey: SERVICE_TICKET_KEYS.GET_SERVICE_TICKET(id!),
    queryFn: () => serviceTicketService.getServiceTicket(id!),
    enabled: !!id,
  });
};

export const useGetMyServiceTickets = (params?: GetServiceTicketParams) => {
  return useQuery({
    queryKey: SERVICE_TICKET_KEYS.GET_MY_SERVICE_TICKETS(params),
    queryFn: () => serviceTicketService.getMyServiceTickets(params),
  });
};

export const useGetMyServiceTicketsInfinite = (
  params?: GetServiceTicketParams
) => {
  return useInfiniteQuery({
    queryKey: SERVICE_TICKET_KEYS.GET_MY_SERVICE_TICKETS_INFINITE(params),
    queryFn: ({ pageParam = 1 }) =>
      serviceTicketService.getMyServiceTickets({ ...params, page: pageParam }),
    getNextPageParam: (lastPage) => {
      if (lastPage.meta.currentPage < lastPage.meta.totalPages) {
        return lastPage.meta.currentPage + 1;
      }
      return undefined;
    },
    initialPageParam: params?.page ?? 1,
  });
};

export const useGetUpcomingServiceTicketsByInventoryId = (
  inventoryId: string | undefined,
  params?: GetServiceTicketParams
) => {
  return useQuery({
    queryKey: SERVICE_TICKET_KEYS.GET_UPCOMING_SERVICE_TICKETS_BY_INVENTORY_ID(
      inventoryId || "",
      params
    ),
    queryFn: () =>
      serviceTicketService.getUpcomingServiceTicketsByInventoryId(
        inventoryId || "",
        params
      ),
    enabled: !!inventoryId,
  });
};

export const useGetHistoryServiceTicketsByInventoryId = (
  inventoryId: string | undefined,
  params?: GetServiceTicketParams
) => {
  return useQuery({
    queryKey: SERVICE_TICKET_KEYS.GET_HISTORY_SERVICE_TICKETS_BY_INVENTORY_ID(
      inventoryId || "",
      params
    ),
    queryFn: () =>
      serviceTicketService.getHistoryServiceTicketsByInventoryId(
        inventoryId || "",
        params
      ),
    enabled: !!inventoryId,
  });
};

export const useGetMyServiceTicketInventories = (id?: string | null) => {
  return useQuery({
    queryKey: SERVICE_TICKET_KEYS.GET_SERVICE_TICKET(id!),
    queryFn: () => serviceTicketService.getServiceTicketInventories(id!),
    enabled: !!id,
  });
};
